/**
 * EVO — marshrut chegaralari: yuklanish va xato.
 *
 * ============================================================================
 * NIMA UCHUN BU FAYL BOR
 * ============================================================================
 * Ekranlar `lazyRouteComponent` bilan alohida chunk'ga ajratilgan
 * (`routes/index.tsx`). Bu ikkita YANGI holat tug'diradi, ular ilgari bo'lmagan:
 *
 *   1. Chunk hali kelmagan — ekran bir zumga bo'sh qoladi.
 *   2. Chunk kelmadi — tarmoq uzildi yoki deploy paytida eski fayl yo'qoldi.
 *      Bu chinakam hol: ochiq turgan ilova soatlab ishlaydi, shu vaqt ichida
 *      server yangi versiyaga o'tadi va eski chunk manzili 404 qaytaradi.
 *
 * Ikkalasi ham marshrutlarda emas, ROUTER darajasida ulanadi
 * (`defaultPendingComponent` / `defaultErrorComponent`). Sabab: 45 ta
 * marshrutning har biriga qo'lda yozilsa, 46-marshrutda unutiladi va faqat
 * foydalanuvchi oq ekran ko'rganda bilinadi.
 */
import type { ReactNode } from 'react';
import type { ErrorComponentProps } from '@tanstack/react-router';
import { useRouter } from '@tanstack/react-router';
import { useT } from '../lib/i18n-context';
import { Alert } from './ui/alert';
import { Button } from './ui/button';
import { Spinner } from './ui/spinner';

/**
 * Chunk yuklanayotgandagi holat.
 *
 * `min-h-64` — `NotFoundPage` bilan bir xil balandlik. Bu ataylab: kontent
 * maydoni bo'shab qolganda ham o'lchamini saqlaydi, ya'ni spinner o'rniga
 * ekran kelganda sahifa «sakramaydi».
 *
 * ESLATMA: router buni DARHOL ko'rsatmaydi — `defaultPendingMs` (1 soniya)
 * gacha eski ekran joyida turadi. Tez tarmoqda foydalanuvchi spinnerni umuman
 * ko'rmaydi va bu to'g'ri: 80 ms uchun miltillagan spinner shovqin.
 */
export function RoutePending(): ReactNode {
  const t = useT();

  return (
    <div className="flex min-h-64 items-center justify-center gap-3 text-ink-muted">
      <Spinner />
      <span className="text-sm">{t('common.loading')}</span>
    </div>
  );
}

/**
 * Marshrut yiqilgandagi holat: chunk kelmadi yoki ekran render paytida xato berdi.
 *
 * Ikki tugma, chunki ikki xil sabab va ular BOSHQA yechim talab qiladi:
 *
 *   • «Qayta urinish» — `router.invalidate()`. Tarmoq bir zumga uzilgan bo'lsa
 *     shu yetadi; ochiq sahifa va foydalanuvchi kiritgan holat saqlanadi.
 *     `reset()` YOLG'IZ yetarli emas: u faqat React chegarasini tozalaydi,
 *     marshrut esa hamon `error` holatida va darhol yana yiqiladi.
 *   • «Qayta yuklash» — to'liq `location.reload()`. Deploy'dan keyin eski chunk
 *     yo'qolgan bo'lsa, qayta urinish HAR SAFAR o'sha yo'q faylni so'raydi;
 *     faqat yangi `index.html` yangi chunk nomlarini olib keladi.
 */
export function RouteError({ error, reset }: ErrorComponentProps): ReactNode {
  const t = useT();
  const router = useRouter();

  // Xom xabar operator uchun emas, qo'ng'iroq qilgan dasturchi uchun: «Failed to
  // fetch dynamically imported module» bilan «Cannot read properties of
  // undefined» butunlay boshqa muammolar, lekin ekranda bir xil ko'rinadi.
  const detail = error instanceof Error ? error.message : undefined;

  return (
    <div className="flex min-h-64 flex-col items-center justify-center gap-4 p-8">
      <Alert tone="danger" title={t('error.screenFailed')} className="max-w-xl">
        <p>{t('error.screenFailedHint')}</p>
        {detail && <p className="mt-2 break-words font-mono text-xs text-ink-muted">{detail}</p>}
      </Alert>

      <div className="flex gap-2">
        <Button
          variant="secondary"
          onClick={() => {
            void router.invalidate();
            reset();
          }}
        >
          {t('common.retry')}
        </Button>
        <Button onClick={() => window.location.reload()}>{t('common.reload')}</Button>
      </div>
    </div>
  );
}
